import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { DownloadSimple, TrendUp, ShoppingCart, Users, SimCard, Lightning } from "@phosphor-icons/react";
import api, { API } from "../../lib/api";
import { fmtEUR, flag, countryName } from "../../lib/format";

function Stat({ label, value, sub, icon: Icon, accent = "cobalt", testid }) {
  const palette = {
    cobalt: "bg-cobalt-50 text-cobalt-600",
    coral: "bg-coral-soft/60 text-coral-deep",
    mint: "bg-mint-soft text-mint",
    sun: "bg-sun-soft text-amber-700",
    berry: "bg-berry-soft text-berry",
  };
  return (
    <div className="card-soft !rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${palette[accent]}`}>
          <Icon size={20} weight="bold" />
        </div>
      </div>
      <div className="mt-4">
        <div className="font-display font-bold text-3xl tracking-tight">{value}</div>
        <div className="text-xs text-ink-500 mt-1 uppercase tracking-widest">{label}</div>
        {sub && <div className="text-xs text-ink-500 mt-2">{sub}</div>}
      </div>
    </div>
  );
}

function ExportButton({ kind, label, testid }) {
  const dl = async () => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API}/admin/export/${kind}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${kind}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <button onClick={dl} className="btn-outline text-sm !py-2 !px-3" data-testid={testid}>
      <DownloadSimple size={14} /> {label}
    </button>
  );
}

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => { api.get("/admin/analytics?days=30").then(({ data }) => setAnalytics(data)); }, []);

  if (!analytics) {
    return <div className="text-ink-500" data-testid="admin-dashboard-loading">{t("common.loading")}</div>;
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline mb-2">{t("common.last30Days")}</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">{t("admin.stats")}</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <ExportButton kind="orders" label={t("admin.orders") + " · " + t("common.export")} testid="export-orders" />
          <ExportButton kind="customers" label={t("admin.customers") + " · " + t("common.export")} testid="export-customers" />
          <ExportButton kind="esims" label={t("admin.esims") + " · " + t("common.export")} testid="export-esims" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat testid="stat-revenue" label={t("admin.revenue")} value={fmtEUR(analytics.revenue)} sub={`${analytics.orders_paid} paid orders`} icon={TrendUp} accent="cobalt" />
        <Stat testid="stat-aov" label={t("common.aov")} value={fmtEUR(analytics.aov)} icon={ShoppingCart} accent="coral" />
        <Stat testid="stat-conv" label={t("common.conversion")} value={`${analytics.conversion_rate}%`} sub={`${analytics.orders_total} sessions`} icon={Lightning} accent="mint" />
        <Stat testid="stat-fulfill" label={t("common.fulfillment")} value={`${analytics.fulfillment_rate}%`} sub={`${analytics.orders_failed} failed`} icon={SimCard} accent="sun" />
        <Stat testid="stat-newcust" label={t("common.newCustomers")} value={analytics.new_customers} icon={Users} accent="berry" />
        <Stat testid="stat-topups" label={t("common.topupCount")} value={analytics.topups} icon={Lightning} accent="coral" />
        <Stat testid="stat-fulfilled" label={t("admin.fulfilled")} value={analytics.orders_fulfilled} icon={SimCard} accent="mint" />
        <Stat testid="stat-failed" label={t("admin.failed")} value={analytics.orders_failed} icon={SimCard} accent="cobalt" />
      </div>

      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4 mt-8">
        <div className="card-soft !rounded-2xl p-6" data-testid="chart-revenue">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">{t("common.chartRevenue")}</div>
              <div className="font-display font-bold text-2xl tracking-tight">{fmtEUR(analytics.revenue)}</div>
            </div>
            <span className="pill bg-cobalt-50 text-cobalt-600">{t("common.last30Days")}</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={analytics.series}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} />
              <Tooltip formatter={(v) => `€${v}`} contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB" }} />
              <Area type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2.5} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card-soft !rounded-2xl p-6" data-testid="chart-orders">
          <div className="overline mb-1">{t("common.chartOrders")}</div>
          <div className="font-display font-bold text-2xl tracking-tight mb-4">{analytics.orders_paid}</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={analytics.series}>
              <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E7EB" }} />
              <Bar dataKey="orders" fill="#FB923C" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div className="card-soft !rounded-2xl p-6" data-testid="top-countries">
          <div className="overline mb-4">{t("common.topCountries")}</div>
          <div className="space-y-3">
            {analytics.top_countries.length === 0 && <div className="text-sm text-ink-500">—</div>}
            {analytics.top_countries.map((c) => (
              <div key={c.iso2 + c.country} className="flex items-center gap-3">
                <span className="text-2xl">{flag(c.iso2)}</span>
                <div className="flex-1">
                  <div className="font-semibold">{countryName(c.iso2, i18n.language, c.country)}</div>
                  <div className="text-xs text-ink-500">{c.orders} orders</div>
                </div>
                <div className="font-display font-bold">{fmtEUR(c.revenue)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-soft !rounded-2xl p-6" data-testid="top-plans">
          <div className="overline mb-4">{t("common.topPlans")}</div>
          <div className="space-y-3">
            {analytics.top_plans.length === 0 && <div className="text-sm text-ink-500">—</div>}
            {analytics.top_plans.map((p) => (
              <div key={p.plan} className="flex items-center justify-between gap-3">
                <span className="font-medium">{p.plan}</span>
                <span className="pill bg-cobalt-50 text-cobalt-600">{p.orders}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
